import { useRemaining } from "../hooks/useServerNow";
import { formatSeconds } from "../state/selectors";

/** Plain text countdown to a server deadline. Renders nothing without a deadline. */
export function Countdown({
  deadline,
  prefix,
  className,
}: {
  deadline: number | null | undefined;
  prefix?: string;
  className?: string;
}) {
  const left = useRemaining(deadline);
  if (left === null) return null;
  return (
    <span className={className} aria-live="off">
      {prefix}
      {formatSeconds(left)}
    </span>
  );
}

/** A ring that drains as the deadline approaches, with the seconds in the middle. */
export function CountdownRing({
  deadline,
  totalMs,
  size = 96,
}: {
  deadline: number | null | undefined;
  totalMs: number;
  size?: number;
}) {
  const left = useRemaining(deadline);
  if (left === null) return null;
  const stroke = 6;
  const r = (size - stroke) / 2;
  const circumference = 2 * Math.PI * r;
  const fraction = totalMs > 0 ? Math.min(1, left / totalMs) : 0;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="timer">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--line)" strokeWidth={stroke} />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke={fraction < 0.3 ? "var(--danger)" : "var(--accent)"}
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={circumference * (1 - fraction)}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        style={{ transition: "stroke-dashoffset 0.25s linear" }}
      />
      <text
        x="50%"
        y="50%"
        textAnchor="middle"
        dominantBaseline="central"
        fill="var(--fg)"
        fontSize={size * 0.32}
        fontWeight={700}
      >
        {formatSeconds(left)}
      </text>
    </svg>
  );
}
